import { useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryFetch } from '@services/api';
import { useJobRefetch } from '@hooks/job/refetch';
import { queryKeys } from '@utils/consts';
import {
  applyOptimisticScheduleDrop,
  OptimisticScheduleDrop,
  PreviousScheduleQueriesData,
  restoreScheduleQueriesData,
} from '@services/workload/calendar/helpers';
import { pauseRevalidation, resumeRevalidation } from '@services/revalidateQueries';
import { WorkloadCalendarRespSuccess } from '@services/workload/calendar/types';
import { InternalSchedule, InternalScheduleReq, InternalScheduleRespSuccess } from './types';
import { camelToSnakeCase } from '@utils/textHelpers';

type RescheduleInternalScheduleVariables = InternalScheduleReq & {
  optimisticDropData?: OptimisticScheduleDrop;
};

export const useRescheduleInternalSchedule = ({
  id,
  callback,
}: {
  id: string;
  callback: (data: InternalScheduleRespSuccess) => void;
}) => {
  const { refetch } = useJobRefetch({ schedule: { type: 'internal', action: 'update' } });
  const queryClient = useQueryClient();
  const previousDataRef = useRef<PreviousScheduleQueriesData>(undefined);

  const { mutate, isPending } = useMutation({
    mutationFn: ({
      optimisticDropData: _optimisticDropData,
      ...data
    }: RescheduleInternalScheduleVariables): Promise<InternalScheduleRespSuccess> => {
      const { scheduleId, schedule } = data;
      const { crewChangeScope } = schedule;
      return queryFetch({
        url: `jobs/${id}/internal_schedules/${scheduleId}/reschedule`,
        data: {
          ...data,
          schedule: {
            ...schedule,
            crewChangeScope: camelToSnakeCase(crewChangeScope),
          } as InternalSchedule,
        },
        opt: { method: 'POST' },
      });
    },
    onMutate: async ({ optimisticDropData }) => {
      if (!optimisticDropData) return;
      pauseRevalidation(queryKeys.workloadWeeklyCalendar);
      await queryClient.cancelQueries({ queryKey: queryKeys.workloadWeeklyCalendar });
      const previousData = queryClient.getQueriesData<WorkloadCalendarRespSuccess>({
        queryKey: queryKeys.workloadWeeklyCalendar,
      });
      previousDataRef.current = previousData;
      queryClient.setQueriesData<WorkloadCalendarRespSuccess>(
        { queryKey: queryKeys.workloadWeeklyCalendar },
        (old) => applyOptimisticScheduleDrop(old, optimisticDropData),
      );
      return { previousData };
    },
    onError: (err, _variables, context) => {
      console.error(err);
      restoreScheduleQueriesData(queryClient, context?.previousData);
      previousDataRef.current = undefined;
      resumeRevalidation(queryKeys.workloadWeeklyCalendar);
    },
    onSuccess: async ({ success }) => {
      if (!success) return;
      previousDataRef.current = undefined;
      resumeRevalidation(queryKeys.workloadWeeklyCalendar);
      await refetch();
    },
    onSettled: (data) => {
      if (data) callback(data);
    },
  });

  const discardOptimisticDropDataUpdate = async () => {
    if (previousDataRef.current) {
      restoreScheduleQueriesData(queryClient, previousDataRef.current);
      previousDataRef.current = undefined;
    }
    resumeRevalidation(queryKeys.workloadWeeklyCalendar);
    await refetch();
  };

  return {
    mutate,
    isLoading: isPending,
    discardOptimisticDropDataUpdate,
  };
};
